/**
 * 사용자가 파일을 원하는지, 그 표를 어디까지 담을지 정한다.
 *
 * 짐작하지 않고 **사용자가 쓴 낱말**만 본다. 형식 판단을 LLM 에 맡기면 판단이 하나
 * 늘고, 이 프로젝트에서 LLM 판단을 얹었다가 잡음에 묻힌 적이 있다.
 */

export type FileFormat = 'xlsx' | 'docx' | 'pdf' | 'pptx';

export type TableData = {
  columns: string[];
  rows: unknown[][];
  /** 상한에 걸려 버린 행 수. 자른 적이 없으면 없다. */
  truncated?: number;
  /** 자료 읽기(`reading.ts`)를 이 표의 칸 이름으로 푼 것. 열린 문제로 읽힌 표는 상태 칸 짐작과 경과 장치를 끈다. */
  reading?: import('./reading').TableReading;
};

/** 분야 전문가의 갈래 계산 하나 — 코드가 원자료 전체로 센 값과, 센 칸(화면의 칸 이름). */
export type ResultAxis = { axis: string; columns: string[]; value: unknown };

export type ResultSnapshot = {
  id: string;
  question: string;
  summary: unknown;
  table?: TableData;
  /** 갈래 계산 결과 — 다음 턴 문서가 그 숫자를 근거로 쓰고 출처 칸을 밝힌다(7차 검토, 2026-10-24). */
  axes?: ResultAxis[];
  /**
   * 보고서가 셀 모집단: 계산 표와 같은 칸으로 조회한 행 전부(달마다 조회했으면 「대상월」 칸). 계산 표가 행을 덜 담았거나
   * 달 칸이 없을 때만 있다. 계산이 「비고가 있거나 미실시인」 1,828행을 표로 내 보고서가 그것을 「합계」로 실었다
   * (2026-10-25 10:11). 〈표 1〉·경과·묶음·그림은 이것으로 세고, 상세 행은 계산 표를 쓴다.
   */
  population?: TableData;
  /**
   * 계산 전에 뺀 계정의 줄(연차 자료의 이름에 숫자가 붙은 계정, `listTables.ts`의 `duplicateAccounts`). 다음 턴 보고서가 요약 끝에
   * 싣는다. 표에는 그 계정이 없다.
   */
  excluded?: string[];
  /**
   * 계획이 비워 둔 선택 칸에 조회 때 걸려 있던 화면 값(「연차생성기준 입사일」). 다음 턴 문서가 연차 생성 기준을 계산 요약의 글에만
   * 기대지 않는다.
   */
  screenValues?: string[];
  /** 사용자 말대로 이름에 숫자가 붙은 계정을 남겼다(「숫자 붙은 계정까지 넣어서」). 다음 턴 문서도 빼지 않는다. */
  keepNumbered?: boolean;
  /**
   * 계산 표에 남긴 이름에 숫자가 붙은 계정(묻기, 넣기, 넣기 상태, 이름을 말한 질문). 다음 턴 문서가 이 표를 그대로 쓸 때 넣고 빼기를
   * 맞추고, 넣어서 셌다는 문장의 이름으로 쓴다.
   */
  numbered?: string[];
  /** 조회한 표의 자료 읽기(칸 id와 화면 이름). 다음 턴 문서가 다시 묻지 않고 같은 뜻으로 센다. */
  reading?: import('./reading').DataReading;
  sources: string[];
  createdAt: string;
  /** 같은 결과를 다른 형식으로 만들 때 원래 업무 목표를 잃지 않는다. */
  task?: import('./task').TaskSpec;
};

/** 보고서의 막대 그래프 하나. 범주마다 계열 값이 하나씩 있다. */
export type ReportChart = {
  /** 시간 흐름(달이 셋 이상)은 선, 2~3칸의 구성(완료·미실시)은 100% 누적 막대 하나, 나머지는 막대(없음). */
  kind?: 'line' | 'share' | 'stack';
  title: string;
  categories: string[];
  /** 범주마다 값 하나. `null` 은 그 범주에 자료가 없다는 뜻이다 — 0 과 다르다. */
  series: { name: string; values: (number | null)[] }[];
  /** 값의 단위(건·원·명·%). 그림 제목 끝에 「(단위: 건)」으로 붙는다. */
  unit?: string;
  /** 이 그래프가 보여 주는 결론 한 문장. 있으면 제목 자리에 쓰고 `title` 은 작은 설명으로 내린다. */
  message?: string;
  /** 강조색을 칠할 계열(미실시 등). 없으면 계열마다 차례 색이다. */
  highlight?: string;
};

export type ReportDocument = {
  title: string;
  period: string;
  audience?: string;
  cadence?: string;
  summaryHeading?: string;
  detailHeading?: string;
  accent?: string;
  kpis: { label: string; value: string }[];
  summary: string;
  /** 결론 한 줄. 있으면 제목 자리에 이것을 쓴다 — 「ERP 현황 보고서」는 아무것도 말하지 않는다. */
  headline?: string;
  /** 보고 문서의 칸 — □ 요약 · □ 확인 필요 사항 · □ 향후 조치(안). 개조식 문장, 기호는 렌더러가 붙인다. */
  points?: { summary: string[]; checks: string[]; actions: string[] };
  /** 기준일 — 「'26. 10. 23.(금) 기준」. 제목 바로 아래에 쓴다. */
  basis?: string;
  /** 이름에 숫자가 붙은 계정을 남기고 만든 문서인가. 다음 턴이 넣고 빼기를 바꾸면 이 문서를 다시 쓰지 않는다. */
  keepNumbered?: boolean;
  /** 이 문서를 쓴 자료 읽기. 다음 문서 턴(보고서 뒤 발표자료)이 같은 뜻으로 쓴다(독립 재검토: 턴마다 물으면 엇갈릴 수 있다). */
  reading?: import('./reading').DataReading;
  /** 상태 코드의 뜻 — 「결과 「계획」 = 미실시」. ※ 판정 줄에 쓴다. */
  statusRule?: string;
  /** 작성 부서·작성자. 앱이 로그인한 사람으로 채운다. */
  author?: string;
  /** 공지의 문의처와 발신 부서. 로그인한 사람의 부서다(사용자 확인, 2026-10-27). 비면 자리표시(「(담당 부서)」)다. */
  unit?: string;
  /** 합계 요약표(구분·전체·상태별·비율). */
  statusTable?: TableData;
  /** 예외 행(기한 경과 미실시 등) 다섯까지와 전체 수. */
  /** ※ 줄 — 갈래 숫자를 어느 칸으로 셌는지(「서명 집계: 고객싸인·담당싸인 칸 기준」). 예전 문서만 — 지금은 lineNotes. */
  axisNotes?: string[];
  /** 그 줄 바로 아래 * 줄 — 갈래 숫자를 센 칸. 문서 끝 ※ 묶음에서는 어느 줄의 근거인지 안 보였다(8차 검토). */
  lineNotes?: { line: string; note: string }[];
  /** 문서에 쓴 갈래 값 — 보고서 데이터만으로 그 숫자를 대조할 수 있게(8차 검토). */
  axes?: ResultAxis[];
  /** 〈표 1〉 아래 한 줄 — 미실시 중 경과와 예정일 이내(코드가 센다). */
  statusNote?: string;
  /** 예외 행(기한 경과 미실시 등)과 전체 수. points 는 〈표 2〉 위 쏠림 줄(코드가 쓴다). */
  exceptions?: {
    title: string; table: TableData; total: number; listed?: number; points?: string[];
    /** 표의 단위 — 두 달 판은 「호기, 건」, units 는 호기 수(10차 검토). */
    unit?: string; units?: number;
  };
  /** ※ 줄 — 원자료 자체의 결함(없는 날짜 20260931 등). ERP 에서 고칠 일이라 사용자에게도 알린다(10차 검토). */
  dataNotes?: string[];
  /**
   * 〈표 1〉·〈표 2〉를 센 모집단(조회한 행 전부). 보고서 데이터만으로 검산할 수 있게 남긴다. table은 1턴 계산 표라
   * 완료 1,952건을 그것으로는 셀 수 없었다(12차 검토).
   */
  population?: TableData;
  /** 메모 확인에 센 현장마다 묶음·호기 수·달·담당·메모 원문(13차 검토: 「15개 현장」을 보고서만으로 검산할 수 없었다). */
  memos?: { family: string; name: string; site: string; months: string[]; units: number; owners: string[]; memo: string }[];
  /**
   * 점검 자료가 아닌 목록(직원·현장별 수치)의 표: 묶음별 현황(groups), 주 수치 상위 행(top). 코드가 센다. 연차 74명 명단이
   * 공지와 발표자료에 한 줄도 안 실렸다(실측 2026-10-26). groups는 그림 앞, top은 그림 뒤에 놓는다.
   */
  tables?: { role: 'groups' | 'top'; caption: string; table: TableData }[];
  /**
   * 문서 종류. 공지(사내 게시용 안내문)는 게시물 꼴이다: 수신, 번호 항목(points.summary 다음 points.actions, 끝에 문의처), 〈참고〉 표,
   * 게시일과 발신 부서. 없으면 보고.
   */
  genre?: 'notice';
  /**
   * 발표자료 꼴. 마케팅형(keynote)은 어두운 바탕에 한 장 한 생각, 큰 숫자, 강조 막대 하나. 없으면 보고용(한국형 보고서 장표).
   * PDF와 워드는 이 값을 보지 않는다.
   */
  deck?: 'report' | 'keynote';
  charts?: ReportChart[];
  /**
   * 문서(PDF·워드·발표자료)에 원자료 표를 넣는가. 0 이면 넣지 않는다(요약표·예외 행만), 없으면 전부를 별첨으로.
   * 원자료 앞 몇 행은 넣지 않는다 — 무작위 표본은 보고에 뜻이 없다(국가직 팀장 검토, 2026-10-23). 엑셀은 늘 전체다.
   */
  detailRows?: number;
  table?: TableData;
  methodology: string;
  sources: string[];
  generatedAt: string;
  style?: string;
};

/** 브라우저가 xlsx 를 만드는 데 드는 메모리와 시간을 감당할 수 있는 선. */
export const MAX_TABLE_ROWS = 50000;
export const MAX_TABLE_CELLS = 500000;

const WORDS: { format: FileFormat; pattern: RegExp }[] = [
  { format: 'xlsx', pattern: /엑셀|excel|xlsx|스프레드시트/i },
  { format: 'docx', pattern: /워드|word|docx?|문서로/i },
  { format: 'pdf', pattern: /pdf/i },
  { format: 'pptx', pattern: /발표\s*자료|프레젠테이션|pptx?|파워\s*포인트|슬라이드/i },
];

export function requestedFormats(question: string): FileFormat[] {
  return WORDS.filter((entry) => entry.pattern.test(question)).map((entry) => entry.format);
}

/** 칸 이름 끝의 내부 칸 이름 — 「결과(state)」. 단위(원·%·kg·EA)와는 소문자 세 글자 이상인 것으로 가른다. */
const INTERNAL_NAME = /\s*\(([a-z][a-z0-9]*(?:_[a-z0-9]+)*)\)$/;
const UNITS = ['ton', 'sec', 'min', 'kwh', 'mwh', 'ppm', 'rpm'];

/**
 * 사람에게 보이는 칸 이름. 계산이 붙인 내부 칸 이름은 뗀다 — ※ 판정에 「결과(state)」가 실렸다(실제 테넌트, 2026-10-25).
 * 떼면 다른 칸과 이름이 같아지면 둔다.
 */
function plainColumns(columns: string[]): string[] {
  const plain = columns.map((column) => {
    // 코드 풀이 괄호(「결과(%=전체, 1=계획, 2=완료)」)도 뗀다. 모집단 칸 이름이 「결과(%=전체」로 남았다(2026-10-25 10:42).
    const legend = /\([^()]*=[^()]*\)?$/.exec(column);
    if (legend && legend.index > 0) return column.slice(0, legend.index).trim();
    const match = INTERNAL_NAME.exec(column);
    return match && (match[1] ?? '').length >= 3 && UNITS.indexOf(match[1] ?? '') < 0 && match.index > 0
      ? column.slice(0, match.index) : column;
  });
  return plain.map((column, index) => (plain.indexOf(column) === index && columns.indexOf(column) < 0
    ? column : columns[index] ?? column));
}

/** 넘치면 자르되 **몇 행을 버렸는지 남긴다.** 조용히 자르면 전체를 받은 줄 안다. */
export function capTable(table: TableData): TableData {
  const columns = plainColumns(table.columns);
  const width = Math.max(1, columns.length);
  const limit = Math.min(MAX_TABLE_ROWS, Math.floor(MAX_TABLE_CELLS / width));
  if (table.rows.length <= limit) return { ...table, columns };
  return {
    columns,
    rows: table.rows.slice(0, limit),
    truncated: table.rows.length - limit,
  };
}
